import type { ReactElement } from "react";
import type { LamaPillarResultsOutput } from "@playbookai/widget-schemas";
import { Progress } from "../../atoms/Progress/Progress.js";
import { Sparkline } from "../../atoms/Sparkline/Sparkline.js";
import { TrendIndicator } from "../../atoms/TrendIndicator/TrendIndicator.js";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
  lamaCategoryHistoryLabel,
  lamaDeltaLabel,
  lamaDeltaPoints,
  lamaHistoryHasGap,
  lamaScorePercentLabel,
  lamaScoreToPercent,
} from "../lama-report-presentation.js";

/**
 * LAMA Results by Category — the per-category rows ported data-blind from
 * `LamaReportPage`: name, progress on the 0–5 scale with a previous-wave tick,
 * score, "was x.xx", trend, sparkline. The previous value rides the progress
 * bar's `aria-label` so it reaches assistive tech at any width; the visible
 * "was x.xx" is layout-gated at `@xl/content`.
 */
export function LamaCategoryResultsWidget({
  data,
}: WidgetBodyProps<LamaPillarResultsOutput>): ReactElement {
  // Suppression first: no rows for an anonymity reason, not for lack of scores.
  if (data.suppressed) {
    return (
      <div>
        <p className="text-sm font-medium text-pb-text">
          {LAMA_SUPPRESSED_TITLE}
        </p>
        <p className="mt-1 text-sm text-pb-text-muted">
          {LAMA_SUPPRESSED_BODY}
        </p>
      </div>
    );
  }
  if (data.rows.length === 0) {
    return (
      <p className="text-sm text-pb-text-muted">
        No category results available — they will appear when report snapshots
        contain scored areas.
      </p>
    );
  }
  return (
    <div className="divide-y divide-pb-border rounded-lg border border-pb-border">
      {data.rows.map((row) => (
        <div
          key={row.pillar}
          className="flex flex-wrap items-center gap-3 px-4 py-3"
        >
          <span className="min-w-36 flex-1 text-sm font-medium text-pb-text">
            {row.pillar}
          </span>
          {/* min-w-32 flex-1 lives on the wrapper: Progress forwards
              className to its own outer div, so leaving it there would
              unsize every bar. */}
          <div className="relative min-w-32 flex-1">
            {row.current != null ? (
              <Progress
                value={lamaScoreToPercent(row.current)}
                max={100}
                aria-label={`${row.pillar} maturity${
                  row.previous != null
                    ? `, previous wave ${lamaScorePercentLabel(row.previous)}`
                    : ""
                }`}
              />
            ) : (
              // An empty track, announced as "Unavailable".
              <Progress value={null} aria-label={`${row.pillar} maturity`} />
            )}
            {row.current != null && row.previous != null ? (
              <span
                aria-hidden="true"
                className="absolute top-0 -ms-px h-1.5 w-0.5 bg-pb-text-muted"
                style={{
                  insetInlineStart: `${lamaScoreToPercent(row.previous)}%`,
                }}
              />
            ) : null}
          </div>
          <span className="w-10 text-end text-sm pb-tabular">
            {lamaScorePercentLabel(row.current)}
          </span>
          {row.previous != null ? (
            <span className="hidden text-xs text-pb-text-muted @xl/content:inline">
              was {lamaScorePercentLabel(row.previous)}
            </span>
          ) : null}
          {row.delta != null ? (
            <TrendIndicator
              delta={lamaDeltaPoints(row.delta)}
              unit=" pts"
              aria-label={`${row.pillar}: ${lamaDeltaLabel(row.delta)}`}
            />
          ) : (
            <span className="text-xs text-pb-text-muted">—</span>
          )}
          {row.spark.length > 1 ? (
            <span className="inline-flex items-center gap-1">
              <Sparkline
                data={row.spark.map(lamaScoreToPercent)}
                tone={row.delta != null && row.delta < 0 ? "critical" : "info"}
                width={84}
                height={24}
                aria-hidden="true"
              />
              {row.history !== undefined && lamaHistoryHasGap(row.history) ? (
                /* The sparkline joins its points evenly, so an interior gap is
                   drawn as a continuous line — a trend across a wave that never
                   measured this category. The marker says so where a sighted
                   reader sees the line; the sr-only history below says which
                   wave. Task 21. */
                <span
                  className="text-xs text-pb-text-muted"
                  title="One or more waves did not measure this category"
                >
                  ⚠
                </span>
              ) : null}
            </span>
          ) : (
            <span className="w-21" aria-hidden="true" />
          )}
          {row.history !== undefined ? (
            /* The sparkline is `aria-hidden`, so this is the ONLY history a
               screen reader gets. Before Task 21 there was none — just the
               current score and one delta. */
            <span className="sr-only">
              {lamaCategoryHistoryLabel(row.pillar, row.history)}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}
